import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/SuperAdminDashboard.css';

const API_URL = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
const PLAN_OPTIONS = [
  { value: 'basico', label: 'BÁSICO' },
  { value: 'premium', label: 'PREMIUM' },
  { value: 'premium_gold', label: 'PREMIUM GOLD' }
];

function SuperAdminDashboard() {
  const [tenants, setTenants] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [credencialesLoading, setCredencialesLoading] = useState(false);
  const [credencialesForm, setCredencialesForm] = useState({
    admin_username: '',
    nueva_password: '',
    confirmar_password: ''
  });
  const [planEnProceso, setPlanEnProceso] = useState(null);
  const navigate = useNavigate();

  const [newTenant, setNewTenant] = useState({
    nombre_completo: '',
    nombre_negocio: '',
    codigo: '',
    celular_whatsapp: '',
    email: '',
    rnc: '',
    direccion: '',
    admin_username: '',
    admin_password: ''
  });

  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      const token = localStorage.getItem('super_admin_token');
      if (!token) {
        navigate('/super-admin/login');
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [tenantsRes, dashboardRes] = await Promise.all([
        axios.get(`${API_URL}/super-admin/tenants`, config),
        axios.get(`${API_URL}/super-admin/dashboard`, config)
      ]);

      setTenants(tenantsRes.data.tenants);
      setEstadisticas(dashboardRes.data.estadisticas);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        navigate('/super-admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCrearTenant = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.post(
        `${API_URL}/super-admin/tenants`,
        newTenant,
        config
      );

      alert(`✅ Tenant creado exitosamente!\n\nCredenciales:\nUsuario: ${response.data.credenciales.username}\nContraseña: ${response.data.credenciales.password}\n\n⚠️ Guarda estas credenciales, el cliente las necesitará.`);

      setShowCreateModal(false);
      setNewTenant({
        nombre_completo: '',
        nombre_negocio: '',
        codigo: '',
        celular_whatsapp: '',
        email: '',
        rnc: '',
        direccion: '',
        admin_username: '',
        admin_password: ''
      });
      cargarDatos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Error al crear tenant'));
    }
  };

  const handleSuspenderTenant = async (tenantId) => {
    const razon = prompt('Razón de la suspensión:');
    if (!razon) return;

    try {
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(
        `${API_URL}/super-admin/tenants/${tenantId}/suspender`,
        { razon },
        config
      );

      alert('✅ Tenant suspendido exitosamente');
      cargarDatos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Error al suspender'));
    }
  };

  const handleActivarTenant = async (tenantId) => {
    try {
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(
        `${API_URL}/super-admin/tenants/${tenantId}/activar`,
        {},
        config
      );

      alert('✅ Tenant activado exitosamente');
      cargarDatos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Error al activar'));
    }
  };

  const handleResetearPassword = async (tenantId) => {
    if (!window.confirm('¿Estás seguro de resetear la contraseña del administrador?')) {
      return;
    }

    try {
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.put(
        `${API_URL}/super-admin/tenants/${tenantId}/resetear-password`,
        {},
        config
      );

      alert(`✅ Contraseña reseteada\n\nUsuario: ${response.data.username}\nNueva contraseña: ${response.data.nueva_password}\n\n⚠️ Comparte esta información con el cliente.`);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Error al resetear contraseña'));
    }
  };

  const handleAbrirCredenciales = async (tenant) => {
    try {
      setCredencialesLoading(true);
      setSelectedTenant(tenant);

      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(
        `${API_URL}/super-admin/tenants/${tenant.id}`,
        config
      );

      const tenantData = response.data;
      setSelectedTenant(tenantData);
      setCredencialesForm({
        admin_username: tenantData.admin_username || '',
        nueva_password: '',
        confirmar_password: ''
      });
      setShowCredentialsModal(true);
    } catch (error) {
      alert('Error al cargar la información del administrador');
    } finally {
      setCredencialesLoading(false);
    }
  };

  const handleActualizarPlan = async (tenantId, nuevoPlan) => {
    if (!nuevoPlan) return;

    const confirmar = window.confirm(`¿Cambiar el plan de este cliente a ${nuevoPlan.toUpperCase()}?`);
    if (!confirmar) return;

    try {
      setPlanEnProceso(`${tenantId}-${nuevoPlan}`);
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.put(
        `${API_URL}/super-admin/tenants/${tenantId}`,
        { plan: nuevoPlan },
        config
      );

      alert('✅ Plan actualizado correctamente');
      cargarDatos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'No se pudo actualizar el plan'));
    } finally {
      setPlanEnProceso(null);
    }
  };

  const handleCerrarCredenciales = () => {
    setShowCredentialsModal(false);
    setSelectedTenant(null);
    setCredencialesForm({
      admin_username: '',
      nueva_password: '',
      confirmar_password: ''
    });
    setCredencialesLoading(false);
  };

  const handleActualizarCredenciales = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;

    const username = credencialesForm.admin_username.trim();

    if (!username) {
      alert('El usuario administrador es obligatorio');
      return;
    }

    if (credencialesForm.nueva_password) {
      if (credencialesForm.nueva_password.length < 6) {
        alert('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      if (credencialesForm.nueva_password !== credencialesForm.confirmar_password) {
        alert('La confirmación de la contraseña no coincide');
        return;
      }
    }

    try {
      setCredencialesLoading(true);
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const payload = { admin_username: username };
      if (credencialesForm.nueva_password) {
        payload.nueva_password = credencialesForm.nueva_password;
      }

      const response = await axios.put(
        `${API_URL}/super-admin/tenants/${selectedTenant.id}/credenciales`,
        payload,
        config
      );

      const { username: nuevoUsuario, nueva_password: nuevaClave, password_actualizada } = response.data;

      let mensaje = `✅ Credenciales actualizadas.\n\nUsuario: ${nuevoUsuario}`;
      if (password_actualizada && nuevaClave) {
        mensaje += `\nNueva contraseña: ${nuevaClave}`;
      } else if (password_actualizada) {
        mensaje += `\nLa contraseña fue actualizada.`;
      } else {
        mensaje += `\nLa contraseña no se modificó.`;
      }

      alert(mensaje);
      handleCerrarCredenciales();
      cargarDatos();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'No se pudieron actualizar las credenciales'));
    } finally {
      setCredencialesLoading(false);
    }
  };

  const handleEnviarMensaje = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('super_admin_token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.post(
        `${API_URL}/super-admin/tenants/${selectedTenant.id}/enviar-mensaje`,
        { mensaje },
        config
      );

      alert('✅ Mensaje enviado. El cliente lo verá en su dashboard.');
      setShowMessageModal(false);
      setMensaje('');
      setSelectedTenant(null);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Error al enviar mensaje'));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('super_admin_token');
    localStorage.removeItem('super_admin');
    localStorage.removeItem('is_super_admin');
    navigate('/super-admin/login');
  };

  const filteredTenants = tenants.filter(tenant => {
    const matchSearch = tenant.nombre_negocio.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       tenant.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       tenant.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchEstado = filterEstado === '' || tenant.estado === filterEstado;

    return matchSearch && matchEstado;
  });

  if (loading) {
    return (
      <div className="loading-container">
        <i className="fas fa-spinner fa-spin fa-3x"></i>
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="super-admin-dashboard">
      {/* Header */}
      <header className="super-admin-header">
        <div className="header-left">
          <i className="fas fa-crown"></i>
          <h1>Panel Super Admin</h1>
        </div>
        <div className="header-right">
          <span className="admin-name">
            <i className="fas fa-user-shield"></i> kilvio
          </span>
          <button className="btn btn-logout" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i> Salir
          </button>
        </div>
      </header>

      {/* Estadísticas */}
      {estadisticas && (
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon blue">
              <i className="fas fa-building"></i>
            </div>
            <div className="stat-info">
              <h3>{estadisticas.total_tenants}</h3>
              <p>Total Clientes</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <h3>{estadisticas.tenants_activos}</h3>
              <p>Activos</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange">
              <i className="fas fa-pause-circle"></i>
            </div>
            <div className="stat-info">
              <h3>{estadisticas.tenants_suspendidos}</h3>
              <p>Suspendidos</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon red">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <div className="stat-info">
              <h3>{estadisticas.tenants_con_pago_pendiente}</h3>
              <p>Pago Pendiente</p>
            </div>
          </div>
        </div>
      )}

      {/* Controles */}
      <div className="controls-container">
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          <i className="fas fa-plus"></i> Nuevo Cliente
        </button>

        <div className="filters">
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Buscar por nombre, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <select
            className="form-control"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
          >
            <option value="">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="suspendido">Suspendidos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Tabla de Tenants */}
      <div className="tenants-table-container">
        <table className="tenants-table">
          <thead>
            <tr>
              <th>Negocio</th>
              <th>Dueño</th>
              <th>Contacto</th>
              <th>Estado</th>
              <th>Plan</th>
              <th>Clientes</th>
              <th>Préstamos</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredTenants.map(tenant => (
              <tr key={tenant.id}>
                <td>
                  <strong>{tenant.nombre_negocio}</strong>
                  <br />
                  <small className="text-muted">{tenant.slug}</small>
                </td>
                <td>{tenant.nombre_completo}</td>
                <td>
                  <div><i className="fas fa-phone"></i> {tenant.celular_whatsapp}</div>
                  <div><i className="fas fa-envelope"></i> {tenant.email}</div>
                </td>
                <td>
                  <span className={`badge badge-${tenant.estado === 'activo' ? 'success' : 'danger'}`}>
                    {tenant.estado}
                  </span>
                  {tenant.pago_pendiente && (
                    <span className="badge badge-warning ml-2">
                      <i className="fas fa-exclamation-circle"></i> Pago Pendiente
                    </span>
                  )}
                </td>
                <td>
                  <select
                    className="form-control plan-select"
                    value={tenant.plan || 'basico'}
                    onChange={(e) => handleActualizarPlan(tenant.id, e.target.value)}
                    disabled={planEnProceso?.startsWith(`${tenant.id}-`)}
                    title="Cambiar plan"
                  >
                    {PLAN_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{tenant.total_clientes || 0}</td>
                <td>{tenant.total_prestamos || 0}</td>
                <td>
                  <div className="action-buttons">
                    {tenant.estado === 'activo' ? (
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleSuspenderTenant(tenant.id)}
                        title="Suspender"
                      >
                        <i className="fas fa-pause"></i>
                        <span>Suspender</span>
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleActivarTenant(tenant.id)}
                        title="Activar"
                      >
                        <i className="fas fa-play"></i>
                        <span>Activar</span>
                      </button>
                    )}

                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => {
                        setSelectedTenant(tenant);
                        setShowMessageModal(true);
                      }}
                      title="Enviar mensaje"
                    >
                      <i className="fas fa-envelope"></i>
                      <span>Mensaje</span>
                    </button>

                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleResetearPassword(tenant.id)}
                      title="Resetear contraseña"
                    >
                      <i className="fas fa-key"></i>
                      <span>Resetear clave</span>
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleAbrirCredenciales(tenant)}
                      title="Editar credenciales"
                    >
                      <i className="fas fa-user-edit"></i>
                      <span>Credenciales</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTenants.length === 0 && (
          <div className="no-data">
            <i className="fas fa-inbox fa-3x"></i>
            <p>No se encontraron clientes</p>
          </div>
        )}
      </div>

      {/* Modal Crear Tenant */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><i className="fas fa-plus-circle"></i> Crear Nuevo Cliente</h2>
              <button onClick={() => setShowCreateModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleCrearTenant}>
              <div className="form-row">
                <div className="form-group">
                  <label>👤 Nombre Completo *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTenant.nombre_completo}
                    onChange={(e) => setNewTenant({...newTenant, nombre_completo: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🏛️ Nombre del Negocio/Empresa *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTenant.nombre_negocio}
                    onChange={(e) => setNewTenant({...newTenant, nombre_negocio: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🔖 Código de Acceso * <small>(ej: invramirez, invjulio)</small></label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTenant.codigo}
                    onChange={(e) => setNewTenant({...newTenant, codigo: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '')})}
                    placeholder="invramirez"
                    required
                    pattern="[a-z0-9]+"
                  />
                  <small className="text-muted">Este será el código para acceder: localhost:3000/{newTenant.codigo || 'codigo'}</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>☎️ Celular con WhatsApp *</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={newTenant.celular_whatsapp}
                    onChange={(e) => setNewTenant({...newTenant, celular_whatsapp: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>✉️ Correo Electrónico *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={newTenant.email}
                    onChange={(e) => setNewTenant({...newTenant, email: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>📄 RNC (Opcional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTenant.rnc}
                    onChange={(e) => setNewTenant({...newTenant, rnc: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>📍 Dirección (Opcional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTenant.direccion}
                    onChange={(e) => setNewTenant({...newTenant, direccion: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>👤 Usuario de Acceso *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newTenant.admin_username}
                    onChange={(e) => setNewTenant({...newTenant, admin_username: e.target.value})}
                    placeholder="ej: inversionesramirez"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>🔑 Contraseña *</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newTenant.admin_password}
                    onChange={(e) => setNewTenant({...newTenant, admin_password: e.target.value})}
                    placeholder="Contraseña para el cliente"
                    required
                    minLength="6"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save"></i> Crear Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCredentialsModal && selectedTenant && (
        <div className="modal-overlay" onClick={handleCerrarCredenciales}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-user-cog"></i> Credenciales de {selectedTenant.nombre_negocio}
              </h2>
              <button onClick={handleCerrarCredenciales}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleActualizarCredenciales}>
              <div className="form-group">
                <label>👤 Usuario administrador *</label>
                <input
                  type="text"
                  className="form-control"
                  value={credencialesForm.admin_username}
                  onChange={(e) => setCredencialesForm({
                    ...credencialesForm,
                    admin_username: e.target.value
                  })}
                  required
                />
              </div>

              <div className="form-group">
                <label>🔑 Nueva contraseña (opcional)</label>
                <input
                  type="password"
                  className="form-control"
                  value={credencialesForm.nueva_password}
                  onChange={(e) => setCredencialesForm({
                    ...credencialesForm,
                    nueva_password: e.target.value
                  })}
                  placeholder="Deja en blanco para mantener la actual"
                  minLength={credencialesForm.nueva_password ? 6 : undefined}
                />
              </div>

              <div className="form-group">
                <label>🔁 Confirmar contraseña</label>
                <input
                  type="password"
                  className="form-control"
                  value={credencialesForm.confirmar_password}
                  onChange={(e) => setCredencialesForm({
                    ...credencialesForm,
                    confirmar_password: e.target.value
                  })}
                  placeholder="Repite la nueva contraseña"
                  minLength={credencialesForm.nueva_password ? 6 : undefined}
                />
              </div>

              <p className="text-muted">
                Si solo deseas cambiar el usuario, deja los campos de contraseña vacíos.
              </p>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCerrarCredenciales}
                  disabled={credencialesLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={credencialesLoading}
                >
                  {credencialesLoading ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Enviar Mensaje */}
      {showMessageModal && selectedTenant && (
        <div className="modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-envelope"></i> Enviar Mensaje a {selectedTenant.nombre_negocio}
              </h2>
              <button onClick={() => setShowMessageModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleEnviarMensaje}>
              <div className="form-group">
                <label>Mensaje</label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  placeholder="Este mensaje se mostrará en el dashboard del cliente..."
                  required
                />
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowMessageModal(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-paper-plane"></i> Enviar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
